import React, { useState, useEffect, useRef, useId } from 'react';
import { fetchExternalRecipients } from '../features/externalRecipients/externalRecipientsApi';
import { ExternalRecipient } from '../features/externalRecipients/types';

interface ExternalRecipientAutocompleteProps {
  onSelect: (externalRecipientId: string) => void;
  existingRecipientIds?: string[];
}

export function ExternalRecipientAutocomplete({
  onSelect,
  existingRecipientIds = [],
}: ExternalRecipientAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<ExternalRecipient[]>([]);
  const [rawCount, setRawCount] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;

  // Debounced search
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      if (query.length < 2) {
        setResults([]);
        setRawCount(0);
        setIsOpen(false);
        return;
      }
      setSearching(true);
      setIsOpen(true);
      try {
        const response = await fetchExternalRecipients({ search: query, pageSize: 20, signal: controller.signal });
        setRawCount(response.data.length);
        const filtered = response.data.filter(
          (r) => !existingRecipientIds.includes(r.id)
        );
        setResults(filtered);
      } catch (error) {
        if (controller.signal.aborted) return;
        setResults([]);
        setRawCount(0);
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, existingRecipientIds]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (recipient: ExternalRecipient) => {
    onSelect(recipient.id);
    setQuery('');
    setResults([]);
    setRawCount(0);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && query.length >= 2;

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-2">
        Agregar destinatario:
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-autocomplete="list"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (query.length >= 2) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Buscar por email, nombre o apellido..."
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      />

      {/* Dropdown */}
      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto"
        >
          {searching ? (
            <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
              Buscando...
            </div>
          ) : results.length > 0 ? (
            results.map((recipient) => (
              <button
                key={recipient.id}
                role="option"
                aria-selected="false"
                onClick={() => handleSelect(recipient)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent focus:bg-accent focus:outline-none"
              >
                <span className="font-medium">{recipient.firstName} {recipient.lastName}</span>
                <span className="ml-2 text-muted-foreground">{recipient.email}</span>
                <span className="ml-2 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {recipient.role}
                </span>
              </button>
            ))
          ) : rawCount > 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Todos los destinatarios encontrados ya fueron agregados
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No se encontraron destinatarios
            </div>
          )}
        </div>
      )}
    </div>
  );
}
